import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlusCircle, RefreshCw, Edit, Trash2, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAbility } from '@/components/authnz/AbilityContext';
import { UserRole } from '@/components/authnz/types/rbac';
import { useScopedDb } from '@/contexts/AuthContext';
import { Subscription } from './types/subscription';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import logger from '@/lib/logging';
import {
  deleteSubscriptionApi,
  fetchReconcilePreviewApi,
  syncSubscriptionsFromStripeApi,
} from '@/lib/subscriptions-admin-api';
import { ContextualToolbar, ToolbarButtonGroup } from '@/components/ui/contextual-toolbar';
import { formatDateSafe } from '@/lib/utils';
import SubscriptionForm from './SubscriptionForm';
import BulkSubscriptionAssignment from './BulkSubscriptionAssignment';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DEFAULT_SUBSCRIPTION_STATUS_FILTER,
  SUBSCRIPTION_STATUS_FILTER_OPTIONS,
  matchesSubscriptionStatusFilter,
  type SubscriptionStatusFilter,
} from './lib/subscription-status-filter';

export default function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<Subscription | null>(null);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [reconcileConfirmOpen, setReconcileConfirmOpen] = useState(false);
  const [inactiveReconcileCount, setInactiveReconcileCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatusFilter>(
    DEFAULT_SUBSCRIPTION_STATUS_FILTER
  );
  const { toast } = useToast();
  const ability = useAbility();
  const scopedDb = useScopedDb();

  const loadSubscriptions = async () => {
    try {
      const response = await fetch('/api/protected/subscriptions/all');
      if (!response.ok) {
        throw new Error('Failed to load subscriptions');
      }
      const data = await response.json();
      setSubscriptions(data);
    } catch (error) {
      logger.error('Error loading subscriptions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscriptions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!scopedDb) {
      setLoading(false);
      return;
    }
    loadSubscriptions();
  }, [scopedDb]);

  const runSyncFromStripe = async () => {
    setSyncing(true);
    try {
      const result = await syncSubscriptionsFromStripeApi();
      const report = result.report;
      toast({
        title: 'Reconciled from Stripe',
        description: report
          ? `Subs +${report.subscriptions.added} ~${report.subscriptions.updated} canceled ${report.subscriptions.canceled} skipped(local) ${report.subscriptions.skippedLocal}; roles +${report.rolesGranted} -${report.rolesRevoked}`
          : 'Stripe reconcile completed',
      });
      await loadSubscriptions();
    } catch (error: any) {
      logger.error('Error syncing subscriptions:', error);
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync subscriptions from Stripe',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
      setReconcileConfirmOpen(false);
      setInactiveReconcileCount(0);
    }
  };

  const handleSyncFromStripe = async () => {
    // Fail closed: a full reconcile can revoke roles, so it never starts
    // without a successful preview (AUD-05).
    let count: number;
    try {
      ({ inactiveUsersToRegainRoles: count } = await fetchReconcilePreviewApi());
    } catch (error: any) {
      logger.error('Reconcile preview failed; reconcile not started', error);
      toast({
        title: 'Reconcile Not Started',
        description: error?.message
          ? `Could not preview the Stripe reconcile: ${error.message}`
          : 'Could not preview the Stripe reconcile. Please retry.',
        variant: 'destructive',
      });
      return;
    }
    if (count > 0) {
      setInactiveReconcileCount(count);
      setReconcileConfirmOpen(true);
      return;
    }
    await runSyncFromStripe();
  };

  const handleDelete = async () => {
    if (!subscriptionToDelete) return;

    try {
      await deleteSubscriptionApi(subscriptionToDelete.id);

      toast({
        title: 'Subscription Deleted',
        description: 'Subscription has been deleted',
      });
      setSubscriptionToDelete(null);
    } catch (error: any) {
      logger.error('Error deleting subscription:', error);
      toast({
        title: 'Delete Failed',
        description: error.message || 'Failed to delete subscription',
        variant: 'destructive',
      });
      setSubscriptionToDelete(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      trialing: 'outline',
      canceled: 'destructive',
      expired: 'destructive',
      past_due: 'destructive',
      incomplete: 'secondary',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const canManage = ability.can('manage', 'Subscription');
  const canCreate = ability.can('create', 'Subscription');
  const canMutateRow = (subscription: Subscription) =>
    canManage || subscription.userRole === UserRole.PRACTITIONER;
  const filteredSubscriptions = subscriptions.filter((subscription) =>
    matchesSubscriptionStatusFilter(subscription, statusFilter)
  );

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Bulk Assignment Section */}
      {canCreate && <BulkSubscriptionAssignment />}

      <ContextualToolbar position="sticky-top">
        <ToolbarButtonGroup align="right">
          {canManage && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-10"
                onClick={handleSyncFromStripe}
                disabled={syncing}
              >
                <RefreshCw className={`h-4 w-4 sm:mr-2 ${syncing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Reconcile from Stripe</span>
              </Button>
            </>
          )}
          {canCreate && (
            <Button
              size="sm"
              className="h-10"
              onClick={() => {
                setEditingSubscription(null);
                setShowForm(true);
              }}
            >
              <PlusCircle className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Subscription</span>
            </Button>
          )}
        </ToolbarButtonGroup>
      </ContextualToolbar>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-primary">Subscriptions</CardTitle>
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as SubscriptionStatusFilter)}
          >
            <SelectTrigger className="w-[160px]" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUBSCRIPTION_STATUS_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No subscriptions in local mirror</p>
              {canManage && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={handleSyncFromStripe}
                  disabled={syncing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Reconcile from Stripe
                </Button>
              )}
            </div>
          ) : filteredSubscriptions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No subscriptions match this filter</p>
            </div>
          ) : (
            <div className="overflow-x-auto touch-pan-xy">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>User Emails</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Type</TableHead>
                    {canCreate && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell className="font-medium">{subscription.productId}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {subscription.userEmails.slice(0, 2).map((email, idx) => (
                            <span key={idx} className="text-sm">{email}</span>
                          ))}
                          {subscription.userEmails.length > 2 && (
                            <span className="text-xs text-muted-foreground">
                              +{subscription.userEmails.length - 2} more
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{subscription.userRole}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(subscription.status)}</TableCell>
                      <TableCell>
                        {formatDateSafe(subscription.startDate, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        {subscription.isLifetime || !subscription.currentPeriodEnd
                          ? '—'
                          : formatDateSafe(subscription.currentPeriodEnd, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>
                        {subscription.isLifetime ? (
                          <Badge variant="default">Lifetime</Badge>
                        ) : (
                          <Badge variant="secondary">Recurring</Badge>
                        )}
                      </TableCell>
                      {canCreate && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Edit subscription"
                              disabled={!canMutateRow(subscription)}
                              onClick={() => {
                                setEditingSubscription(subscription);
                                setShowForm(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Cancel subscription"
                              disabled={!canMutateRow(subscription)}
                              onClick={() => setSubscriptionToDelete(subscription)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSubscription ? 'Edit Subscription' : 'Create Subscription'}
            </DialogTitle>
          </DialogHeader>
          <SubscriptionForm
            subscription={editingSubscription || undefined}
            onSuccess={() => {
              setShowForm(false);
              setEditingSubscription(null);
            }}
            onCancel={() => {
              setShowForm(false);
              setEditingSubscription(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={reconcileConfirmOpen} onOpenChange={setReconcileConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore access for inactive users?</AlertDialogTitle>
            <AlertDialogDescription>
              Full Stripe reconcile will grant roles to {inactiveReconcileCount} inactive user
              {inactiveReconcileCount === 1 ? '' : 's'} who have active Stripe-backed subscriptions.
              Banned users are never granted roles.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={syncing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void runSyncFromStripe()} disabled={syncing}>
              Reconcile anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!subscriptionToDelete} onOpenChange={(open) => !open && setSubscriptionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              This cancels the local subscription and revokes roles from assigned users. The record remains with status canceled.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

